import { lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthGuard, GuestGuard } from '@/shared/auth/guards';
import { useAuth } from '@/shared/auth/store';
import { homePathForRoles } from '@/shared/auth/role-home';
import { AppShell } from '@/shared/ui/app-shell';
import { PageErrorBoundary } from '@/shared/ui/page-error-boundary';

/**
 * Root `/` — pick the landing page based on the user's primary role.
 * Owner/Manager land on the ERP dashboard; Accountant on the accounting
 * dashboard; Salesperson on the CRM my-pipeline view; Employee on
 * self-service. Kept as a component (not a `<Navigate>`) so the target
 * is computed from live auth state instead of frozen at router
 * construction.
 */
function RoleHomeRedirect() {
  const roles = useAuth((s) => s.roles);
  return <Navigate to={homePathForRoles(roles)} replace />;
}

/**
 * Wrap React.lazy so a chunk-load failure (typical after a deploy where the
 * currently-open tab still references old hashed asset filenames) auto-reloads
 * the page once instead of crashing into the "Failed to fetch dynamically
 * imported module" error screen. sessionStorage guards against an infinite
 * reload loop if the chunk is genuinely broken. Same signature as
 * `React.lazy`.
 */
function lazyReloadable<T extends { default: ComponentType<any> }>(factory: () => Promise<T>) {
  return lazy(() => factory().catch((err) => {
    const key = 'chunk-reload-once';
    const already = typeof window !== 'undefined' && window.sessionStorage.getItem(key) === '1';
    const isChunkErr = err instanceof Error && /dynamically imported module|Failed to fetch|Loading chunk/i.test(err.message);
    if (isChunkErr && typeof window !== 'undefined' && !already) {
      window.sessionStorage.setItem(key, '1');
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw err;
  }));
}

if (typeof window !== 'undefined') {
  window.addEventListener('load', () => window.sessionStorage.removeItem('chunk-reload-once'));
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */
const LoginPage = lazyReloadable(() => import('@/modules/core/pages/LoginPage'));
const RegisterPage = lazyReloadable(() => import('@/modules/core/pages/RegisterPage'));
const ForgotPage = lazyReloadable(() => import('@/modules/core/pages/ForgotPage'));

/* -------------------------------------------------------------------------- */
/* Core                                                                       */
/* -------------------------------------------------------------------------- */
const DashboardPage = lazyReloadable(() => import('@/modules/core/pages/DashboardPage'));
const ProfilePage = lazyReloadable(() => import('@/modules/core/pages/ProfilePage'));
const NotificationsPage = lazyReloadable(() => import('@/modules/core/pages/NotificationsPage'));
const ApprovalsPage = lazyReloadable(() => import('@/modules/core/pages/ApprovalsPage'));
const HelpTourPage = lazyReloadable(() => import('@/modules/core/pages/HelpTourPage'));

/* Admin */
const UsersRolesPage = lazyReloadable(() => import('@/modules/core/pages/UsersRolesPage'));
const UserInvitePage = lazyReloadable(() => import('@/modules/core/pages/UserInvitePage'));
const SessionsPage = lazyReloadable(() => import('@/modules/core/pages/SessionsPage'));
const LoginHistoryPage = lazyReloadable(() => import('@/modules/core/pages/LoginHistoryPage'));
const NumberingSettingsPage = lazyReloadable(() => import('@/modules/core/pages/NumberingSettingsPage'));
const GlobalSettingsPage = lazyReloadable(() => import('@/modules/core/pages/GlobalSettingsPage'));
const TerminologyPage = lazyReloadable(() => import('@/modules/core/pages/TerminologyPage'));
const ImportPage = lazyReloadable(() => import('@/modules/core/pages/ImportPage'));
const AuditLogPage = lazyReloadable(() => import('@/modules/core/pages/AuditLogPage'));
const DemoDataPage = lazyReloadable(() => import('@/modules/core/pages/DemoDataPage'));
const LoginLinksPage = lazyReloadable(() => import('@/modules/core/pages/LoginLinksPage'));

/* -------------------------------------------------------------------------- */
/* Organization                                                               */
/* -------------------------------------------------------------------------- */
const CompaniesPage = lazyReloadable(() => import('@/modules/organization/pages/CompaniesPage'));
const BranchesPage = lazyReloadable(() => import('@/modules/organization/pages/BranchesPage'));
const DepartmentsPage = lazyReloadable(() => import('@/modules/organization/pages/DepartmentsPage'));
const DepartmentDetailPage = lazyReloadable(() => import('@/modules/organization/pages/DepartmentDetailPage'));

/* -------------------------------------------------------------------------- */
/* Master Data                                                                */
/* -------------------------------------------------------------------------- */
const CustomersPage = lazyReloadable(() => import('@/modules/masterdata/pages/CustomersPage'));
const CustomerDetailPage = lazyReloadable(() => import('@/modules/masterdata/pages/CustomerDetailPage'));
const SuppliersPage = lazyReloadable(() => import('@/modules/masterdata/pages/SuppliersPage'));
const SupplierDetailPage = lazyReloadable(() => import('@/modules/masterdata/pages/SupplierDetailPage'));
const WarehousesPage = lazyReloadable(() => import('@/modules/masterdata/pages/WarehousesPage'));
const WarehouseDetailPage = lazyReloadable(() => import('@/modules/masterdata/pages/WarehouseDetailPage'));
const TaxesPage = lazyReloadable(() => import('@/modules/masterdata/pages/TaxesPage'));
const PaymentTermsPage = lazyReloadable(() => import('@/modules/masterdata/pages/PaymentTermsPage'));
const UomsPage = lazyReloadable(() => import('@/modules/masterdata/pages/UomsPage'));
const CategoriesPage = lazyReloadable(() => import('@/modules/masterdata/pages/CategoriesPage'));
const TransportersPage = lazyReloadable(() => import('@/modules/masterdata/pages/TransportersPage'));
const CurrenciesPage = lazyReloadable(() => import('@/modules/masterdata/pages/CurrenciesPage'));

/* -------------------------------------------------------------------------- */
/* Product + Inventory                                                       */
/* -------------------------------------------------------------------------- */
const ProductsPage = lazyReloadable(() => import('@/modules/product/pages/ProductsPage'));
const ProductDetailPage = lazyReloadable(() => import('@/modules/product/pages/ProductDetailPage'));
const ProductLabelPage = lazyReloadable(() => import('@/modules/product/pages/ProductLabelPage'));
const StockBalancesPage = lazyReloadable(() => import('@/modules/inventory/pages/StockBalancesPage'));
const StockMovementsPage = lazyReloadable(() => import('@/modules/inventory/pages/StockMovementsPage'));
const InventoryDashboard = lazyReloadable(() => import('@/modules/inventory/pages/InventoryDashboard'));
const StockTransfersPage = lazyReloadable(() => import('@/modules/inventory/pages/StockTransfersPage'));
const StockTransferFormPage = lazyReloadable(() => import('@/modules/inventory/pages/StockTransferFormPage'));
const StockTransferDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/StockTransferDetailPage'));
const StockAdjustmentsPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAdjustmentsPage'));
const StockAdjustmentFormPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAdjustmentFormPage'));
const StockAdjustmentDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAdjustmentDetailPage'));
const BatchDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/BatchDetailPage'));
const CycleCountPage = lazyReloadable(() => import('@/modules/inventory/pages/CycleCountPage'));
const StockAgingPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAgingPage'));
const ReorderSuggestionsPage = lazyReloadable(() => import('@/modules/inventory/pages/ReorderSuggestionsPage'));

/* -------------------------------------------------------------------------- */
/* CRM                                                                        */
/* -------------------------------------------------------------------------- */
const LeadsPage = lazyReloadable(() => import('@/modules/crm/pages/LeadsPage'));
const LeadDetailPage = lazyReloadable(() => import('@/modules/crm/pages/LeadDetailPage'));
const LeadFormPage = lazyReloadable(() => import('@/modules/crm/pages/LeadFormPage'));
const LeadPipelinePage = lazyReloadable(() => import('@/modules/crm/pages/LeadPipelinePage'));
const OpportunitiesPage = lazyReloadable(() => import('@/modules/crm/pages/OpportunitiesPage'));
const OpportunityDetailPage = lazyReloadable(() => import('@/modules/crm/pages/OpportunityDetailPage'));
const OpportunityPipelinePage = lazyReloadable(() => import('@/modules/crm/pages/OpportunityPipelinePage'));
const ActivitiesPage = lazyReloadable(() => import('@/modules/crm/pages/ActivitiesPage'));
const ActivityDetailPage = lazyReloadable(() => import('@/modules/crm/pages/ActivityDetailPage'));
const CrmDashboard = lazyReloadable(() => import('@/modules/crm/pages/CrmDashboard'));

/* -------------------------------------------------------------------------- */
/* Sales                                                                      */
/* -------------------------------------------------------------------------- */
const DeliveryOrdersPage = lazyReloadable(() => import('@/modules/sales/pages/DeliveryOrdersPage'));
const DeliveryOrderDetailPage = lazyReloadable(() => import('@/modules/sales/pages/DeliveryOrderDetailPage'));
const ReceiptDetailPage = lazyReloadable(() => import('@/modules/sales/pages/ReceiptDetailPage'));
const QuotationsPage = lazyReloadable(() => import('@/modules/sales/pages/QuotationsPage'));
const QuotationDetailPage = lazyReloadable(() => import('@/modules/sales/pages/QuotationDetailPage'));
const QuotationFormPage = lazyReloadable(() => import('@/modules/sales/pages/QuotationFormPage'));
const SalesOrdersPage = lazyReloadable(() => import('@/modules/sales/pages/SalesOrdersPage'));
const SalesOrderDetailPage = lazyReloadable(() => import('@/modules/sales/pages/SalesOrderDetailPage'));
const SalesOrderFormPage = lazyReloadable(() => import('@/modules/sales/pages/SalesOrderFormPage'));
const InvoicesPage = lazyReloadable(() => import('@/modules/sales/pages/InvoicesPage'));
const InvoiceDetailPage = lazyReloadable(() => import('@/modules/sales/pages/InvoiceDetailPage'));
const CreditNotesPage = lazyReloadable(() => import('@/modules/sales/pages/CreditNotesPage'));
const CreditNoteDetailPage = lazyReloadable(() => import('@/modules/sales/pages/CreditNoteDetailPage'));
const SalesInvoiceFormPage = lazyReloadable(() => import('@/modules/sales/pages/SalesInvoiceFormPage'));
const CustomerStatementPage = lazyReloadable(() => import('@/modules/sales/pages/CustomerStatementPage'));
const SalesDashboard = lazyReloadable(() => import('@/modules/sales/pages/SalesDashboard'));
const PricingSchemesPage = lazyReloadable(() => import('@/modules/sales/pages/PricingSchemesPage'));
const PricingSchemeFormPage = lazyReloadable(() => import('@/modules/sales/pages/PricingSchemeFormPage'));
const TerritoriesPage = lazyReloadable(() => import('@/modules/sales/pages/TerritoriesPage'));
const SalespersonsPage = lazyReloadable(() => import('@/modules/sales/pages/SalespersonsPage'));
const SalespersonFormPage = lazyReloadable(() => import('@/modules/sales/pages/SalespersonFormPage'));
const CommissionSchemesPage = lazyReloadable(() => import('@/modules/sales/pages/CommissionSchemesPage'));
const CommissionSchemeFormPage = lazyReloadable(() => import('@/modules/sales/pages/CommissionSchemeFormPage'));
const CommissionEarningsPage = lazyReloadable(() => import('@/modules/sales/pages/CommissionEarningsPage'));
const MyPipelinePage = lazyReloadable(() => import('@/modules/sales/pages/MyPipelinePage'));
const SalesLeaderboardPage = lazyReloadable(() => import('@/modules/sales/pages/SalesLeaderboardPage'));
const SalespersonTargetsPage = lazyReloadable(() => import('@/modules/sales/pages/SalespersonTargetsPage'));
const SalespersonTargetFormPage = lazyReloadable(() => import('@/modules/sales/pages/SalespersonTargetFormPage'));
const SalespersonDetailPage = lazyReloadable(() => import('@/modules/sales/pages/SalespersonDetailPage'));

/* -------------------------------------------------------------------------- */
/* Purchase                                                                   */
/* -------------------------------------------------------------------------- */
const PurchaseOrdersPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseOrdersPage'));
const PurchaseOrderFormPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseOrderFormPage'));
const PurchaseOrderDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseOrderDetailPage'));
const PurchaseRequestsPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseRequestsPage'));
const PurchaseRequestFormPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseRequestFormPage'));
const PurchaseRequestDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseRequestDetailPage'));
const GoodsReceiptsPage = lazyReloadable(() => import('@/modules/purchase/pages/GoodsReceiptsPage'));
const GoodsReceiptDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/GoodsReceiptDetailPage'));
const BillsPage = lazyReloadable(() => import('@/modules/purchase/pages/BillsPage'));
const BillDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/BillDetailPage'));
const DebitNotesPage = lazyReloadable(() => import('@/modules/purchase/pages/DebitNotesPage'));
const DebitNoteDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/DebitNoteDetailPage'));
const BillFormPage = lazyReloadable(() => import('@/modules/purchase/pages/BillFormPage'));
const SupplierEvaluationPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierEvaluationPage'));
const PurchaseDashboard = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseDashboard'));
const RfqsPage = lazyReloadable(() => import('@/modules/purchase/pages/RfqsPage'));
const RfqFormPage = lazyReloadable(() => import('@/modules/purchase/pages/RfqFormPage'));
const RfqDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/RfqDetailPage'));
const RfqComparePage = lazyReloadable(() => import('@/modules/purchase/pages/RfqComparePage'));
const SupplierQuotesPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierQuotesPage'));
const SupplierQuoteFormPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierQuoteFormPage'));
const SupplierQuoteDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierQuoteDetailPage'));
const JobWorkOrdersPage = lazyReloadable(() => import('@/modules/purchase/pages/JobWorkOrdersPage'));
const JobWorkOrderFormPage = lazyReloadable(() => import('@/modules/purchase/pages/JobWorkOrderFormPage'));
const JobWorkOrderDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/JobWorkOrderDetailPage'));

/* -------------------------------------------------------------------------- */
/* Manufacturing                                                             */
/* -------------------------------------------------------------------------- */
const ManufacturingDashboard = lazyReloadable(() => import('@/modules/manufacturing/pages/ManufacturingDashboard'));
const BOMPage = lazyReloadable(() => import('@/modules/manufacturing/pages/BOMPage'));
const BOMFormPage = lazyReloadable(() => import('@/modules/manufacturing/pages/BOMFormPage'));
const ProductionOrdersPage = lazyReloadable(() => import('@/modules/manufacturing/pages/ProductionOrdersPage'));
const ShopFloorLivePage = lazyReloadable(() => import('@/modules/manufacturing/pages/ShopFloorLivePage'));
const ProductionOrderFormPage = lazyReloadable(() => import('@/modules/manufacturing/pages/ProductionOrderFormPage'));
const ProductionOrderDetailPage = lazyReloadable(() => import('@/modules/manufacturing/pages/ProductionOrderDetailPage'));
const ProductionBoardPage = lazyReloadable(() => import('@/modules/manufacturing/pages/ProductionBoardPage'));
const JobCardPage = lazyReloadable(() => import('@/modules/manufacturing/pages/JobCardPage'));
const MRPPage = lazyReloadable(() => import('@/modules/manufacturing/pages/MRPPage'));
const ProcessTemplatesPage = lazyReloadable(() => import('@/modules/manufacturing/pages/ProcessTemplatesPage'));
const ProcessTemplateFormPage = lazyReloadable(() => import('@/modules/manufacturing/pages/ProcessTemplateFormPage'));
const FormulasPage = lazyReloadable(() => import('@/modules/manufacturing/pages/FormulasPage'));
const FormulaFormPage = lazyReloadable(() => import('@/modules/manufacturing/pages/FormulaFormPage'));
const WorkCentresPage = lazyReloadable(() => import('@/modules/manufacturing/pages/WorkCentresPage'));
const WorkCentreDetailPage = lazyReloadable(() => import('@/modules/manufacturing/pages/WorkCentreDetailPage'));
const MachinesPage = lazyReloadable(() => import('@/modules/manufacturing/pages/MachinesPage'));
const MachineDetailPage = lazyReloadable(() => import('@/modules/manufacturing/pages/MachineDetailPage'));
const MaterialIssuesPage = lazyReloadable(() => import('@/modules/manufacturing/pages/MaterialIssuesPage'));
const MaterialIssueDetailPage = lazyReloadable(() => import('@/modules/manufacturing/pages/MaterialIssueDetailPage'));
const FinishedGoodsReceiptsPage = lazyReloadable(() => import('@/modules/manufacturing/pages/FinishedGoodsReceiptsPage'));
const FinishedGoodsReceiptDetailPage = lazyReloadable(() => import('@/modules/manufacturing/pages/FinishedGoodsReceiptDetailPage'));
const BatchGenerationSettingsPage = lazyReloadable(() => import('@/modules/core/pages/BatchGenerationSettingsPage'));

/* -------------------------------------------------------------------------- */
/* POS                                                                        */
/* -------------------------------------------------------------------------- */
const PosDashboard = lazyReloadable(() => import('@/modules/pos/pages/PosDashboard'));
const PosTerminalPage = lazyReloadable(() => import('@/modules/pos/pages/PosTerminalPage'));

/* Standalone print view — no AppShell chrome. See shared/features/printable/. */
const PrintPage = lazyReloadable(() => import('@/shared/features/printable/PrintPage'));
const PosSessionsPage = lazyReloadable(() => import('@/modules/pos/pages/PosSessionsPage'));
const PosSalesHistoryPage = lazyReloadable(() => import('@/modules/pos/pages/PosSalesHistoryPage'));
const LoyaltyGiftCardsPage = lazyReloadable(() => import('@/modules/pos/pages/LoyaltyGiftCardsPage'));

/* -------------------------------------------------------------------------- */
/* Accounting                                                                 */
/* -------------------------------------------------------------------------- */
const ChartOfAccountsPage = lazyReloadable(() => import('@/modules/accounting/pages/ChartOfAccountsPage'));
const BudgetsPage = lazyReloadable(() => import('@/modules/accounting/pages/BudgetsPage'));
const BudgetFormPage = lazyReloadable(() => import('@/modules/accounting/pages/BudgetFormPage'));
const JournalPage = lazyReloadable(() => import('@/modules/accounting/pages/JournalPage'));
const JournalEntryFormPage = lazyReloadable(() => import('@/modules/accounting/pages/JournalEntryFormPage'));
const JournalEntryDetailPage = lazyReloadable(() => import('@/modules/accounting/pages/JournalEntryDetailPage'));
const TrialBalancePage = lazyReloadable(() => import('@/modules/accounting/pages/TrialBalancePage'));
const BankAccountsPage = lazyReloadable(() => import('@/modules/accounting/pages/BankAccountsPage'));
const BankAccountDetailPage = lazyReloadable(() => import('@/modules/accounting/pages/BankAccountDetailPage'));
const BankReconciliationPage = lazyReloadable(() => import('@/modules/accounting/pages/BankReconciliationPage'));
const ReceiptsPaymentsPage = lazyReloadable(() => import('@/modules/accounting/pages/ReceiptsPaymentsPage'));
const TallyExportPage = lazyReloadable(() => import('@/modules/accounting/pages/TallyExportPage'));

/* -------------------------------------------------------------------------- */
/* HR / Attendance / Leave / Payroll                                         */
/* -------------------------------------------------------------------------- */
const HrDashboard = lazyReloadable(() => import('@/modules/hr/pages/HrDashboard'));
const EmployeesPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeesPage'));
const OrgChartPage = lazyReloadable(() => import('@/modules/hr/pages/OrgChartPage'));
const EmployeeDetailPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeDetailPage'));
const EmployeeIdCardPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeIdCardPage'));
const EmployeeFormPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeFormPage'));

const AttendanceCalendarPage = lazyReloadable(() => import('@/modules/attendance/pages/AttendanceCalendarPage'));
const AttendanceListPage = lazyReloadable(() => import('@/modules/attendance/pages/AttendanceListPage'));
const SelfServicePage = lazyReloadable(() => import('@/modules/hr/pages/SelfServicePage'));
const MyPipelineCrmPage = lazyReloadable(() => import('@/modules/crm/pages/MyPipelinePage'));

const LeaveApplicationPage = lazyReloadable(() => import('@/modules/leave/pages/LeaveApplicationPage'));
const LeaveApprovalsPage = lazyReloadable(() => import('@/modules/leave/pages/LeaveApprovalsPage'));
const TeamLeaveCalendarPage = lazyReloadable(() => import('@/modules/leave/pages/TeamLeaveCalendarPage'));

const PayrollRunPage = lazyReloadable(() => import('@/modules/payroll/pages/PayrollRunPage'));
const PayrollRunDetailPage = lazyReloadable(() => import('@/modules/payroll/pages/PayrollRunDetailPage'));
const PayslipDetailPage = lazyReloadable(() => import('@/modules/payroll/pages/PayslipDetailPage'));
const SalaryStructuresPage = lazyReloadable(() => import('@/modules/payroll/pages/SalaryStructuresPage'));

/* -------------------------------------------------------------------------- */
/* Fleet / Contract / Rental / Compliance                                    */
/* -------------------------------------------------------------------------- */
const VehiclesPage = lazyReloadable(() => import('@/modules/fleet/pages/VehiclesPage'));
const VehicleFormPage = lazyReloadable(() => import('@/modules/fleet/pages/VehicleFormPage'));
const VehicleDetailPage = lazyReloadable(() => import('@/modules/fleet/pages/VehicleDetailPage'));
const TripsPage = lazyReloadable(() => import('@/modules/fleet/pages/TripsPage'));
const TripDetailPage = lazyReloadable(() => import('@/modules/fleet/pages/TripDetailPage'));
const ContractsPage = lazyReloadable(() => import('@/modules/contract/pages/ContractsPage'));
const ContractDetailPage = lazyReloadable(() => import('@/modules/contract/pages/ContractDetailPage'));
const RentalBookingsPage = lazyReloadable(() => import('@/modules/rental/pages/RentalBookingsPage'));
const RentalBookingDetailPage = lazyReloadable(() => import('@/modules/rental/pages/RentalBookingDetailPage'));
const ComplianceDocumentsPage = lazyReloadable(() => import('@/modules/compliance/pages/ComplianceDocumentsPage'));
const ComplianceDocumentDetailPage = lazyReloadable(() => import('@/modules/compliance/pages/ComplianceDocumentDetailPage'));

/* -------------------------------------------------------------------------- */
/* Service / Field Service                                                   */
/* -------------------------------------------------------------------------- */
const SupportTicketsPage = lazyReloadable(() => import('@/modules/service/pages/SupportTicketsPage'));
const SupportTicketDetailPage = lazyReloadable(() => import('@/modules/service/pages/SupportTicketDetailPage'));
const ServiceVisitsPage = lazyReloadable(() => import('@/modules/service/pages/ServiceVisitsPage'));
const ServiceVisitDetailPage = lazyReloadable(() => import('@/modules/service/pages/ServiceVisitDetailPage'));
const FieldServiceDashboard = lazyReloadable(() => import('@/modules/fieldservice/pages/FieldServiceDashboard'));

/* -------------------------------------------------------------------------- */
/* Projects / Quality / Maintenance                                          */
/* -------------------------------------------------------------------------- */
const ProjectsPage = lazyReloadable(() => import('@/modules/projects/pages/ProjectsPage'));
const ProjectDetailPage = lazyReloadable(() => import('@/modules/projects/pages/ProjectDetailPage'));
const TasksPage = lazyReloadable(() => import('@/modules/projects/pages/TasksPage'));
const InspectionsPage = lazyReloadable(() => import('@/modules/quality/pages/InspectionsPage'));
const InspectionDetailPage = lazyReloadable(() => import('@/modules/quality/pages/InspectionDetailPage'));
const NonConformancesPage = lazyReloadable(() => import('@/modules/quality/pages/NonConformancesPage'));
const NonConformanceDetailPage = lazyReloadable(() => import('@/modules/quality/pages/NonConformanceDetailPage'));
const AssetsPage = lazyReloadable(() => import('@/modules/maintenance/pages/AssetsPage'));
const AssetDetailPage = lazyReloadable(() => import('@/modules/maintenance/pages/AssetDetailPage'));
const MaintenanceRequestsPage = lazyReloadable(() => import('@/modules/maintenance/pages/MaintenanceRequestsPage'));
const MaintenanceRequestDetailPage = lazyReloadable(() => import('@/modules/maintenance/pages/MaintenanceRequestDetailPage'));

/* -------------------------------------------------------------------------- */
/* Logistics                                                                  */
/* -------------------------------------------------------------------------- */
const ShipmentsPage = lazyReloadable(() => import('@/modules/logistics/pages/ShipmentsPage'));
const ShipmentDetailPage = lazyReloadable(() => import('@/modules/logistics/pages/ShipmentDetailPage'));

/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */
const BusinessHealthPage = lazyReloadable(() => import('@/modules/reports/pages/BusinessHealthPage'));
const SalesRegisterPage = lazyReloadable(() => import('@/modules/reports/pages/SalesRegisterPage'));
const CustomerAgingPage = lazyReloadable(() => import('@/modules/reports/pages/CustomerAgingPage'));
const StockValuationPage = lazyReloadable(() => import('@/modules/reports/pages/StockValuationPage'));
const OutletPerformancePage = lazyReloadable(() => import('@/modules/reports/pages/OutletPerformancePage'));
const PurchaseRegisterPage = lazyReloadable(() => import('@/modules/reports/pages/PurchaseRegisterPage'));
const ProfitLossPage = lazyReloadable(() => import('@/modules/reports/pages/ProfitLossPage'));
const BatchCostReport = lazyReloadable(() => import('@/modules/reports/pages/BatchCostReport'));
const ProductCostTrendReport = lazyReloadable(() => import('@/modules/reports/pages/ProductCostTrendReport'));
const MaterialUsageReport = lazyReloadable(() => import('@/modules/reports/pages/MaterialUsageReport'));
const WasteReport = lazyReloadable(() => import('@/modules/reports/pages/WasteReport'));
const RejectionsReport = lazyReloadable(() => import('@/modules/reports/pages/RejectionsReport'));
const YieldReport = lazyReloadable(() => import('@/modules/reports/pages/YieldReport'));
const SalesMarginReport = lazyReloadable(() => import('@/modules/reports/pages/SalesMarginReport'));
const SalespersonPerformancePage = lazyReloadable(() => import('@/modules/reports/pages/SalespersonPerformancePage'));
const BudgetVsActualReport = lazyReloadable(() => import('@/modules/reports/pages/BudgetVsActualReport'));

/* -------------------------------------------------------------------------- */
/* Jewellery (vertical module)                                                */
/*                                                                            */
/* Lazy like every other module, so a tenant without the jewellery module      */
/* never downloads the chunk.                                                  */
/* -------------------------------------------------------------------------- */
const JewelleryDashboardPage = lazyReloadable(() => import('@/modules/jewellery/pages/JewelleryDashboardPage'));
const MetalRatesPage = lazyReloadable(() => import('@/modules/jewellery/pages/MetalRatesPage'));
const JewelleryItemsPage = lazyReloadable(() => import('@/modules/jewellery/pages/JewelleryItemsPage'));
const JewelleryItemDetailPage = lazyReloadable(() => import('@/modules/jewellery/pages/JewelleryItemDetailPage'));
const TagLookupPage = lazyReloadable(() => import('@/modules/jewellery/pages/TagLookupPage'));
const PriceEstimatePage = lazyReloadable(() => import('@/modules/jewellery/pages/PriceEstimatePage'));
const KarigarsPage = lazyReloadable(() => import('@/modules/jewellery/pages/KarigarsPage'));
const KarigarDetailPage = lazyReloadable(() => import('@/modules/jewellery/pages/KarigarDetailPage'));
const KarigarJobsPage = lazyReloadable(() => import('@/modules/jewellery/pages/KarigarJobsPage'));
const SchemeAccountsPage = lazyReloadable(() => import('@/modules/jewellery/pages/SchemeAccountsPage'));
const GirviPage = lazyReloadable(() => import('@/modules/jewellery/pages/GirviPage'));
const MetalStockReportPage = lazyReloadable(() => import('@/modules/jewellery/pages/MetalStockReportPage'));
const HallmarkingReportPage = lazyReloadable(() => import('@/modules/jewellery/pages/HallmarkingReportPage'));
const SchemeAccountDetailPage = lazyReloadable(() => import('@/modules/jewellery/pages/SchemeAccountDetailPage'));
const GirviDetailPage = lazyReloadable(() => import('@/modules/jewellery/pages/GirviDetailPage'));

/* -------------------------------------------------------------------------- */
/* WhatsApp Marketing (addon module)                                          */
/*                                                                            */
/* `WhatsAppLayout` is a lazy layout route wrapping every `/whatsapp/*` page  */
/* in the licence guard, so the wizard / grace banner / expired blocker are   */
/* decided once instead of per page.                                          */
/* -------------------------------------------------------------------------- */
const WhatsAppLayout = lazyReloadable(() => import('@/modules/whatsapp/features/whatsapp-layout'));
const WhatsAppDashboardPage = lazyReloadable(() => import('@/modules/whatsapp/pages/WhatsAppDashboardPage'));
const WhatsAppSetupPage = lazyReloadable(() => import('@/modules/whatsapp/pages/WhatsAppSetupPage'));
const WhatsAppSettingsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/WhatsAppSettingsPage'));
const WhatsAppInboxPage = lazyReloadable(() => import('@/modules/whatsapp/pages/InboxPage'));
const WhatsAppContactsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactsPage'));
const WhatsAppContactDetailPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactDetailPage'));
const WhatsAppContactGroupsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactGroupsPage'));
const WhatsAppTemplatesPage = lazyReloadable(() => import('@/modules/whatsapp/pages/TemplatesPage'));
const WhatsAppTemplateFormPage = lazyReloadable(() => import('@/modules/whatsapp/pages/TemplateFormPage'));
const WhatsAppCampaignsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/CampaignsPage'));
const WhatsAppCampaignFormPage = lazyReloadable(() => import('@/modules/whatsapp/pages/CampaignFormPage'));
const WhatsAppCampaignDetailPage = lazyReloadable(() => import('@/modules/whatsapp/pages/CampaignDetailPage'));
const WhatsAppReportsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ReportsPage'));

/* -------------------------------------------------------------------------- */
/* Module overview dashboards                                                 */
/* -------------------------------------------------------------------------- */
const AccountingDashboard = lazyReloadable(() => import('@/modules/accounting/pages/AccountingDashboard'));
const AttendanceDashboard = lazyReloadable(() => import('@/modules/attendance/pages/AttendanceDashboard'));
const ComplianceDashboard = lazyReloadable(() => import('@/modules/compliance/pages/ComplianceDashboard'));
const ContractDashboard = lazyReloadable(() => import('@/modules/contract/pages/ContractDashboard'));
const ExpenseDashboard = lazyReloadable(() => import('@/modules/expense/pages/ExpenseDashboard'));
const ExpenseClaimsPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseClaimsPage'));
const ExpenseClaimDetailPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseClaimDetailPage'));
const ExpenseClaimFormPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseClaimFormPage'));
const ExpenseCategoriesPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseCategoriesPage'));
const ExpenseAdvancesPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseAdvancesPage'));
const FleetDashboard = lazyReloadable(() => import('@/modules/fleet/pages/FleetDashboard'));
const LeaveDashboard = lazyReloadable(() => import('@/modules/leave/pages/LeaveDashboard'));
const LogisticsDashboard = lazyReloadable(() => import('@/modules/logistics/pages/LogisticsDashboard'));
const MaintenanceDashboard = lazyReloadable(() => import('@/modules/maintenance/pages/MaintenanceDashboard'));
const PayrollDashboard = lazyReloadable(() => import('@/modules/payroll/pages/PayrollDashboard'));
const ProductDashboard = lazyReloadable(() => import('@/modules/product/pages/ProductDashboard'));
const ProjectsDashboard = lazyReloadable(() => import('@/modules/projects/pages/ProjectsDashboard'));
const QualityDashboard = lazyReloadable(() => import('@/modules/quality/pages/QualityDashboard'));
const RentalDashboard = lazyReloadable(() => import('@/modules/rental/pages/RentalDashboard'));
const ReportingDashboard = lazyReloadable(() => import('@/modules/reporting/pages/ReportingDashboard'));
const ServiceDashboard = lazyReloadable(() => import('@/modules/service/pages/ServiceDashboard'));

export const router = createBrowserRouter([
  {
    element: <GuestGuard><Outlet /></GuestGuard>,
    children: [
      { path: '/login', element: <LoginPage /> },
      // A per-industry sign-in link: opens one company and pins the
      // session to it. Replaces the old company dropdown on /login.
      { path: '/login/:loginKey', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot', element: <ForgotPage /> },
    ],
  },
  {
    // Full-screen routes: auth-guarded but NO AppShell (sidebar / topbar).
    // Used for the POS terminal so the register runs edge-to-edge — the app
    // shell chrome would eat 30% of the horizontal space and make a two-line
    // cart at a 1024x768 checkout counter unusable.
    //
    // `/print/:kind/:id` also lives here: it's a standalone HTML print view
    // rendered by the shared <PrintableDoc> letterhead layout — no sidebar
    // or topbar so the browser Print dialog produces a clean sheet. See
    // shared/features/printable/PrintPage.tsx for the dispatch and
    // shared/features/printable/printable.css for the @media print rules
    // that shape the output.
    element: <AuthGuard><Outlet /></AuthGuard>,
    children: [
      { path: '/pos/terminal', element: <PosTerminalPage /> },
      { path: '/print/:kind/:id', element: <PrintPage /> },
    ],
  },
  {
    element: (
      <AuthGuard>
        <AppShell>
          <Outlet />
        </AppShell>
      </AuthGuard>
    ),
    children: [
      { path: '/', element: <RoleHomeRedirect /> },
      { path: '/dashboard', element: <DashboardPage /> },
      // Role-based landings for demo logins. `/self-service` is the
      // employee dashboard (attendance clock-in + leaves + payslips +
      // expense claims + tasks). `/my-pipeline` re-exports the existing
      // rich salesperson pipeline dashboard so the login page's role
      // shortcut has a stable top-level route to redirect to.
      { path: '/self-service', element: <SelfServicePage /> },
      { path: '/my-pipeline', element: <MyPipelineCrmPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/approvals', element: <ApprovalsPage /> },
      {
        path: '/help',
        // Payload-driven page: its content is authored as one data file per
        // vertical, so a thin or surprising entry must never be able to take
        // the whole app down. The boundary contains any render error to one
        // card with a retry.
        element: (
          <PageErrorBoundary label="the help page">
            <HelpTourPage />
          </PageErrorBoundary>
        ),
      },

      // Admin
      { path: '/admin/users', element: <UsersRolesPage /> },
      { path: '/admin/users/invite', element: <UserInvitePage /> },
      { path: '/admin/sessions', element: <SessionsPage /> },
      { path: '/admin/login-history', element: <LoginHistoryPage /> },
      { path: '/admin/numbering', element: <NumberingSettingsPage /> },
      { path: '/admin/global-settings', element: <GlobalSettingsPage /> },
      { path: '/admin/terminology', element: <TerminologyPage /> },
      { path: '/admin/imports', element: <ImportPage /> },
      { path: '/admin/audit', element: <AuditLogPage /> },
      { path: '/admin/demo-data', element: <DemoDataPage /> },
      { path: '/admin/login-links', element: <LoginLinksPage /> },

      // Organization
      { path: '/organization/companies', element: <CompaniesPage /> },
      { path: '/organization/branches', element: <BranchesPage /> },
      { path: '/organization/departments', element: <DepartmentsPage /> },
      { path: '/organization/departments/:id', element: <DepartmentDetailPage /> },

      // Master Data
      { path: '/customers', element: <CustomersPage /> },
      { path: '/customers/:id', element: <CustomerDetailPage /> },
      { path: '/suppliers', element: <SuppliersPage /> },
      { path: '/suppliers/:id', element: <SupplierDetailPage /> },
      { path: '/warehouses', element: <WarehousesPage /> },
      { path: '/warehouses/:id', element: <WarehouseDetailPage /> },
      { path: '/masters/taxes', element: <TaxesPage /> },
      { path: '/masters/payment-terms', element: <PaymentTermsPage /> },
      { path: '/masters/uoms', element: <UomsPage /> },
      { path: '/masters/categories', element: <CategoriesPage /> },
      { path: '/masters/transporters', element: <TransportersPage /> },
      { path: '/masters/currencies', element: <CurrenciesPage /> },

      // Product + Inventory
      { path: '/products/overview', element: <ProductDashboard /> },
      { path: '/products', element: <ProductsPage /> },
      { path: '/products/raw-materials', element: <ProductsPage /> },
      { path: '/products/finished-goods', element: <ProductsPage /> },
      { path: '/products/labels', element: <ProductLabelPage /> },
      { path: '/products/:id/label', element: <ProductLabelPage /> },
      { path: '/products/:id', element: <ProductDetailPage /> },
      { path: '/inventory', element: <InventoryDashboard /> },
      { path: '/inventory/balances', element: <StockBalancesPage /> },
      { path: '/inventory/movements', element: <StockMovementsPage /> },
      { path: '/inventory/transfers', element: <StockTransfersPage /> },
      { path: '/inventory/transfers/new', element: <StockTransferFormPage /> },
      { path: '/inventory/transfers/:id', element: <StockTransferDetailPage /> },
      { path: '/inventory/transfers/:id/edit', element: <StockTransferFormPage /> },
      { path: '/inventory/adjustments', element: <StockAdjustmentsPage /> },
      { path: '/inventory/adjustments/new', element: <StockAdjustmentFormPage /> },
      { path: '/inventory/adjustments/:id', element: <StockAdjustmentDetailPage /> },
      { path: '/inventory/adjustments/:id/edit', element: <StockAdjustmentFormPage /> },
      { path: '/inventory/batches/:id', element: <BatchDetailPage /> },
      { path: '/inventory/cycle-count', element: <CycleCountPage /> },
      { path: '/inventory/aging', element: <StockAgingPage /> },
      { path: '/inventory/reorder', element: <ReorderSuggestionsPage /> },

      // CRM
      { path: '/crm', element: <CrmDashboard /> },
      { path: '/crm/activities', element: <ActivitiesPage /> },
      { path: '/crm/activities/:id', element: <ActivityDetailPage /> },
      { path: '/crm/leads', element: <LeadsPage /> },
      { path: '/crm/leads/pipeline', element: <LeadPipelinePage /> },
      { path: '/crm/leads/new', element: <LeadFormPage /> },
      { path: '/crm/leads/:id/edit', element: <LeadFormPage /> },
      { path: '/crm/leads/:id', element: <LeadDetailPage /> },
      { path: '/crm/opportunities', element: <OpportunitiesPage /> },
      { path: '/crm/opportunities/pipeline', element: <OpportunityPipelinePage /> },
      { path: '/crm/opportunities/:id', element: <OpportunityDetailPage /> },

      // Sales
      { path: '/sales', element: <SalesDashboard /> },
      { path: '/sales/quotations', element: <QuotationsPage /> },
      { path: '/sales/quotations/new', element: <QuotationFormPage /> },
      { path: '/sales/quotations/:id', element: <QuotationDetailPage /> },
      { path: '/sales/quotations/:id/edit', element: <QuotationFormPage /> },
      { path: '/sales/orders', element: <SalesOrdersPage /> },
      { path: '/sales/orders/new', element: <SalesOrderFormPage /> },
      { path: '/sales/orders/:id', element: <SalesOrderDetailPage /> },
      { path: '/sales/deliveries', element: <DeliveryOrdersPage /> },
      { path: '/sales/deliveries/:id', element: <DeliveryOrderDetailPage /> },
      { path: '/sales/invoices', element: <InvoicesPage /> },
      { path: '/sales/invoices/new', element: <SalesInvoiceFormPage /> },
      { path: '/sales/invoices/:id', element: <InvoiceDetailPage /> },
      { path: '/sales/credit-notes', element: <CreditNotesPage /> },
      { path: '/sales/credit-notes/:id', element: <CreditNoteDetailPage /> },
      { path: '/sales/receipts/:id', element: <ReceiptDetailPage /> },
      { path: '/sales/customer-statement', element: <CustomerStatementPage /> },
      { path: '/sales/pricing-schemes', element: <PricingSchemesPage /> },
      { path: '/sales/pricing-schemes/new', element: <PricingSchemeFormPage /> },
      { path: '/sales/pricing-schemes/:id/edit', element: <PricingSchemeFormPage /> },
      { path: '/sales/territories', element: <TerritoriesPage /> },
      { path: '/sales/salespersons', element: <SalespersonsPage /> },
      { path: '/sales/salespersons/new', element: <SalespersonFormPage /> },
      { path: '/sales/salespersons/:id/edit', element: <SalespersonFormPage /> },
      { path: '/sales/salespersons/:id', element: <SalespersonDetailPage /> },
      { path: '/sales/commission-schemes', element: <CommissionSchemesPage /> },
      { path: '/sales/commission-schemes/new', element: <CommissionSchemeFormPage /> },
      { path: '/sales/commission-schemes/:id/edit', element: <CommissionSchemeFormPage /> },
      { path: '/sales/commission-earnings', element: <CommissionEarningsPage /> },
      { path: '/sales/salesperson-targets', element: <SalespersonTargetsPage /> },
      { path: '/sales/salesperson-targets/new', element: <SalespersonTargetFormPage /> },
      { path: '/sales/salesperson-targets/:id/edit', element: <SalespersonTargetFormPage /> },
      { path: '/sales/my-pipeline', element: <MyPipelinePage /> },
      { path: '/sales/leaderboard', element: <SalesLeaderboardPage /> },

      // Purchase
      { path: '/purchase', element: <PurchaseDashboard /> },
      { path: '/purchase/requests', element: <PurchaseRequestsPage /> },
      { path: '/purchase/requests/new', element: <PurchaseRequestFormPage /> },
      { path: '/purchase/requests/:id', element: <PurchaseRequestDetailPage /> },
      { path: '/purchase/requests/:id/edit', element: <PurchaseRequestFormPage /> },
      { path: '/purchase/orders', element: <PurchaseOrdersPage /> },
      { path: '/purchase/orders/new', element: <PurchaseOrderFormPage /> },
      { path: '/purchase/orders/:id', element: <PurchaseOrderDetailPage /> },
      { path: '/purchase/orders/:id/edit', element: <PurchaseOrderFormPage /> },
      { path: '/purchase/grn', element: <GoodsReceiptsPage /> },
      { path: '/purchase/grn/:id', element: <GoodsReceiptDetailPage /> },
      { path: '/purchase/bills', element: <BillsPage /> },
      { path: '/purchase/bills/new', element: <BillFormPage /> },
      { path: '/purchase/bills/:id', element: <BillDetailPage /> },
      { path: '/purchase/debit-notes', element: <DebitNotesPage /> },
      { path: '/purchase/debit-notes/:id', element: <DebitNoteDetailPage /> },
      { path: '/purchase/evaluation', element: <SupplierEvaluationPage /> },
      { path: '/purchase/rfqs', element: <RfqsPage /> },
      { path: '/purchase/rfqs/new', element: <RfqFormPage /> },
      { path: '/purchase/rfqs/:id', element: <RfqDetailPage /> },
      { path: '/purchase/rfqs/:id/edit', element: <RfqFormPage /> },
      { path: '/purchase/rfqs/:id/compare', element: <RfqComparePage /> },
      { path: '/purchase/supplier-quotes', element: <SupplierQuotesPage /> },
      { path: '/purchase/supplier-quotes/new', element: <SupplierQuoteFormPage /> },
      { path: '/purchase/supplier-quotes/:id', element: <SupplierQuoteDetailPage /> },
      { path: '/purchase/job-work', element: <JobWorkOrdersPage /> },
      { path: '/purchase/job-work/new', element: <JobWorkOrderFormPage /> },
      { path: '/purchase/job-work/:id', element: <JobWorkOrderDetailPage /> },

      // Manufacturing
      { path: '/manufacturing', element: <ManufacturingDashboard /> },
      { path: '/manufacturing/boms', element: <BOMPage /> },
      { path: '/manufacturing/boms/new', element: <BOMFormPage /> },
      { path: '/manufacturing/boms/:id/edit', element: <BOMFormPage /> },
      { path: '/manufacturing/production-orders', element: <ProductionOrdersPage /> },
      { path: '/manufacturing/board', element: <ProductionBoardPage /> },
      { path: '/manufacturing/live', element: <ShopFloorLivePage /> },
      { path: '/manufacturing/production-orders/new', element: <ProductionOrderFormPage /> },
      { path: '/manufacturing/production-orders/:id', element: <ProductionOrderDetailPage /> },
      { path: '/manufacturing/production-orders/:id/job-card/:stepId', element: <JobCardPage /> },
      { path: '/manufacturing/work-centres', element: <WorkCentresPage /> },
      { path: '/manufacturing/work-centres/:id', element: <WorkCentreDetailPage /> },
      { path: '/manufacturing/machines', element: <MachinesPage /> },
      { path: '/manufacturing/machines/:id', element: <MachineDetailPage /> },
      { path: '/manufacturing/material-issues', element: <MaterialIssuesPage /> },
      { path: '/manufacturing/material-issues/:id', element: <MaterialIssueDetailPage /> },
      { path: '/manufacturing/finished-goods-receipts', element: <FinishedGoodsReceiptsPage /> },
      { path: '/manufacturing/finished-goods-receipts/:id', element: <FinishedGoodsReceiptDetailPage /> },
      { path: '/manufacturing/mrp', element: <MRPPage /> },
      { path: '/manufacturing/process-templates', element: <ProcessTemplatesPage /> },
      { path: '/manufacturing/process-templates/new', element: <ProcessTemplateFormPage /> },
      { path: '/manufacturing/process-templates/:id/edit', element: <ProcessTemplateFormPage /> },
      { path: '/manufacturing/formulas', element: <FormulasPage /> },
      { path: '/manufacturing/formulas/new', element: <FormulaFormPage /> },
      { path: '/manufacturing/formulas/:id/edit', element: <FormulaFormPage /> },
      { path: '/admin/batch-numbering', element: <BatchGenerationSettingsPage /> },

      // POS — the /pos/terminal route lives OUTSIDE this AppShell group above,
      // so the register runs full-screen without sidebar/topbar chrome.
      { path: '/pos', element: <PosDashboard /> },
      { path: '/pos/sessions', element: <PosSessionsPage /> },
      { path: '/pos/sales-history', element: <PosSalesHistoryPage /> },
      { path: '/pos/loyalty-gift-cards', element: <LoyaltyGiftCardsPage /> },

      // Accounting
      { path: '/accounting', element: <AccountingDashboard /> },
      { path: '/accounting/coa', element: <ChartOfAccountsPage /> },
      { path: '/accounting/journal', element: <JournalPage /> },
      { path: '/accounting/journal/new', element: <JournalEntryFormPage /> },
      { path: '/accounting/journal-entries/:id', element: <JournalEntryDetailPage /> },
      { path: '/accounting/trial-balance', element: <TrialBalancePage /> },
      { path: '/accounting/bank-accounts', element: <BankAccountsPage /> },
      { path: '/accounting/bank-accounts/:id', element: <BankAccountDetailPage /> },
      { path: '/accounting/bank-reconciliation', element: <BankReconciliationPage /> },
      { path: '/accounting/receipts-payments', element: <ReceiptsPaymentsPage /> },
      { path: '/accounting/tally-export', element: <TallyExportPage /> },
      { path: '/accounting/budgets', element: <BudgetsPage /> },
      { path: '/accounting/budgets/new', element: <BudgetFormPage /> },
      { path: '/accounting/budgets/:id/edit', element: <BudgetFormPage /> },

      // Expense
      { path: '/expense', element: <ExpenseDashboard /> },
      { path: '/expense/claims', element: <ExpenseClaimsPage /> },
      { path: '/expense/claims/new', element: <ExpenseClaimFormPage /> },
      { path: '/expense/claims/:id', element: <ExpenseClaimDetailPage /> },
      { path: '/expense/claims/:id/edit', element: <ExpenseClaimFormPage /> },
      { path: '/expense/categories', element: <ExpenseCategoriesPage /> },
      { path: '/expense/advances', element: <ExpenseAdvancesPage /> },

      // HR
      { path: '/hr', element: <HrDashboard /> },
      { path: '/hr/employees', element: <EmployeesPage /> },
      { path: '/hr/employees/new', element: <EmployeeFormPage /> },
      { path: '/hr/employees/id-cards', element: <EmployeeIdCardPage /> },
      { path: '/hr/org-chart', element: <OrgChartPage /> },
      { path: '/hr/employees/:id', element: <EmployeeDetailPage /> },
      { path: '/hr/employees/:id/edit', element: <EmployeeFormPage /> },
      { path: '/hr/employees/:id/id-card', element: <EmployeeIdCardPage /> },

      // Attendance
      { path: '/attendance/overview', element: <AttendanceDashboard /> },
      { path: '/attendance', element: <AttendanceListPage /> },
      { path: '/attendance/calendar', element: <AttendanceCalendarPage /> },

      // Leave
      { path: '/leave/overview', element: <LeaveDashboard /> },
      { path: '/leave', element: <LeaveApprovalsPage /> },
      { path: '/leave/apply', element: <LeaveApplicationPage /> },
      { path: '/leave/team-calendar', element: <TeamLeaveCalendarPage /> },

      // Payroll
      { path: '/payroll/overview', element: <PayrollDashboard /> },
      { path: '/payroll', element: <PayrollRunPage /> },
      { path: '/payroll/runs/:id', element: <PayrollRunDetailPage /> },
      { path: '/payroll/salary-structures', element: <SalaryStructuresPage /> },
      { path: '/payroll/payslips/:id', element: <PayslipDetailPage /> },

      // Fleet
      { path: '/fleet', element: <FleetDashboard /> },
      { path: '/fleet/vehicles', element: <VehiclesPage /> },
      { path: '/fleet/vehicles/new', element: <VehicleFormPage /> },
      { path: '/fleet/vehicles/:id', element: <VehicleDetailPage /> },
      { path: '/fleet/vehicles/:id/edit', element: <VehicleFormPage /> },
      { path: '/fleet/trips', element: <TripsPage /> },
      { path: '/fleet/trips/:id', element: <TripDetailPage /> },

      // Contract
      { path: '/contract/overview', element: <ContractDashboard /> },
      { path: '/contract', element: <ContractsPage /> },
      { path: '/contract/:id', element: <ContractDetailPage /> },

      // Rental
      { path: '/rental/overview', element: <RentalDashboard /> },
      { path: '/rental', element: <RentalBookingsPage /> },
      { path: '/rental/:id', element: <RentalBookingDetailPage /> },

      // Compliance
      { path: '/compliance/overview', element: <ComplianceDashboard /> },
      { path: '/compliance', element: <ComplianceDocumentsPage /> },
      { path: '/compliance/:id', element: <ComplianceDocumentDetailPage /> },

      // Service
      { path: '/service', element: <ServiceDashboard /> },
      { path: '/service/tickets', element: <SupportTicketsPage /> },
      { path: '/service/tickets/:id', element: <SupportTicketDetailPage /> },
      { path: '/service/visits', element: <ServiceVisitsPage /> },
      { path: '/service/visits/:id', element: <ServiceVisitDetailPage /> },

      // Field Service
      { path: '/fieldservice', element: <FieldServiceDashboard /> },

      // Projects
      { path: '/projects/overview', element: <ProjectsDashboard /> },
      { path: '/projects', element: <ProjectsPage /> },
      { path: '/projects/tasks', element: <TasksPage /> },
      { path: '/projects/:id', element: <ProjectDetailPage /> },

      // Quality
      { path: '/quality', element: <QualityDashboard /> },
      { path: '/quality/inspections', element: <InspectionsPage /> },
      { path: '/quality/inspections/:id', element: <InspectionDetailPage /> },
      { path: '/quality/non-conformances', element: <NonConformancesPage /> },
      { path: '/quality/non-conformances/:id', element: <NonConformanceDetailPage /> },

      // Maintenance
      { path: '/maintenance', element: <MaintenanceDashboard /> },
      { path: '/maintenance/assets', element: <AssetsPage /> },
      { path: '/maintenance/assets/:id', element: <AssetDetailPage /> },
      { path: '/maintenance/requests', element: <MaintenanceRequestsPage /> },
      { path: '/maintenance/requests/:id', element: <MaintenanceRequestDetailPage /> },

      // Jewellery — the vertical module.
      //
      // `/jewellery/items/:id` is declared after the literal `lookup` and
      // `estimate` paths so those never get swallowed as an id.
      { path: '/jewellery', element: <JewelleryDashboardPage /> },
      { path: '/jewellery/rates', element: <MetalRatesPage /> },
      { path: '/jewellery/lookup', element: <TagLookupPage /> },
      { path: '/jewellery/estimate', element: <PriceEstimatePage /> },
      { path: '/jewellery/items', element: <JewelleryItemsPage /> },
      { path: '/jewellery/items/:id', element: <JewelleryItemDetailPage /> },
      { path: '/jewellery/karigars', element: <KarigarsPage /> },
      { path: '/jewellery/karigars/:id', element: <KarigarDetailPage /> },
      { path: '/jewellery/karigar-jobs', element: <KarigarJobsPage /> },
      { path: '/jewellery/schemes', element: <SchemeAccountsPage /> },
      { path: '/jewellery/schemes/:id', element: <SchemeAccountDetailPage /> },
      { path: '/jewellery/girvi', element: <GirviPage /> },
      { path: '/jewellery/girvi/:id', element: <GirviDetailPage /> },
      { path: '/jewellery/reports/stock', element: <MetalStockReportPage /> },
      { path: '/jewellery/reports/hallmarking', element: <HallmarkingReportPage /> },

      // WhatsApp Marketing — every child renders inside the licence guard.
      {
        element: <WhatsAppLayout />,
        children: [
          { path: '/whatsapp', element: <WhatsAppDashboardPage /> },
          { path: '/whatsapp/setup', element: <WhatsAppSetupPage /> },
          { path: '/whatsapp/inbox', element: <WhatsAppInboxPage /> },
          { path: '/whatsapp/contacts', element: <WhatsAppContactsPage /> },
          { path: '/whatsapp/contacts/:id', element: <WhatsAppContactDetailPage /> },
          { path: '/whatsapp/groups', element: <WhatsAppContactGroupsPage /> },
          { path: '/whatsapp/templates', element: <WhatsAppTemplatesPage /> },
          { path: '/whatsapp/templates/new', element: <WhatsAppTemplateFormPage /> },
          { path: '/whatsapp/campaigns', element: <WhatsAppCampaignsPage /> },
          { path: '/whatsapp/campaigns/new', element: <WhatsAppCampaignFormPage /> },
          { path: '/whatsapp/campaigns/:id', element: <WhatsAppCampaignDetailPage /> },
          { path: '/whatsapp/campaigns/:id/edit', element: <WhatsAppCampaignFormPage /> },
          { path: '/whatsapp/reports', element: <WhatsAppReportsPage /> },
          { path: '/whatsapp/settings', element: <WhatsAppSettingsPage /> },
        ],
      },

      // Logistics
      { path: '/logistics', element: <LogisticsDashboard /> },
      { path: '/logistics/shipments', element: <ShipmentsPage /> },
      { path: '/logistics/shipments/:id', element: <ShipmentDetailPage /> },

      // Reports
      { path: '/reports', element: <ReportingDashboard /> },
      { path: '/reports/health', element: <BusinessHealthPage /> },
      { path: '/reports/sales-register', element: <SalesRegisterPage /> },
      { path: '/reports/customer-aging', element: <CustomerAgingPage /> },
      { path: '/reports/stock-valuation', element: <StockValuationPage /> },
      { path: '/reports/purchase-register', element: <PurchaseRegisterPage /> },
      { path: '/reports/pnl', element: <ProfitLossPage /> },
      { path: '/reports/batch-cost', element: <BatchCostReport /> },
      { path: '/reports/product-cost-trend', element: <ProductCostTrendReport /> },
      { path: '/reports/material-usage', element: <MaterialUsageReport /> },
      { path: '/reports/waste', element: <WasteReport /> },
      { path: '/reports/rejections', element: <RejectionsReport /> },
      { path: '/reports/yield', element: <YieldReport /> },
      { path: '/reports/sales-margin', element: <SalesMarginReport /> },
      { path: '/reports/salesperson-performance', element: <SalespersonPerformancePage /> },
      { path: '/reports/outlet-performance', element: <OutletPerformancePage /> },
      { path: '/reports/budget-vs-actual', element: <BudgetVsActualReport /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
